import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Package, DollarSign, TrendingUp, Search, 
  ArrowLeft, Box, Layers, AlertTriangle, Clock, RefreshCw, History, Truck
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PartsInventoryPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [spending, setSpending] = useState(null);
  const [usageHistory, setUsageHistory] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isCached, setIsCached] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchSpending();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.get(`${API}/api/parts/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
      setIsCached(res.data.cached || false);
    } catch (err) {
      console.error('Failed to fetch parts dashboard:', err);
      toast.error('Failed to load parts data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpending = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.get(`${API}/api/parts/spending-analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSpending(res.data);
    } catch (err) {
      console.error('Failed to fetch spending analysis:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.post(`${API}/api/parts/refresh-cache`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cache refreshed');
      // Reload data
      await Promise.all([fetchDashboard(), fetchSpending()]);
    } catch (err) {
      console.error('Failed to refresh cache:', err);
      toast.error('Failed to refresh cache');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchUsageHistory = async () => {
    if (usageHistory) return; // Already loaded
    
    setLoadingUsage(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.get(`${API}/api/parts/usage-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsageHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch usage history:', err);
      toast.error('Failed to load usage history');
    } finally {
      setLoadingUsage(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }
    
    setSearching(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.get(`${API}/api/parts/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data);
      setActiveTab('search');
    } catch (err) {
      console.error('Search failed:', err);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parts-inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground">Parts & Inventory</h1>
              {isCached && (
                <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                  Cached
                </span>
              )}
            </div>
            <p className="text-muted-foreground">Inventory analytics and cost tracking</p>
          </div>
        </div>
        
        {/* Search and Refresh */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh data from Fleetio"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Input
            placeholder="Search parts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="w-64"
          />
          <Button onClick={handleSearch} disabled={searching}>
            <Search className="h-4 w-4 mr-2" />
            {searching ? 'Searching...' : 'Search'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {dashboard?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-blue-500/30 bg-blue-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Package className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Parts</p>
                  <p className="text-2xl font-bold">{dashboard.summary.total_parts.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-500/30 bg-green-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <DollarSign className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Catalog Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(dashboard.summary.total_catalog_value)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-purple-500/30 bg-purple-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Layers className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Categories</p>
                  <p className="text-2xl font-bold">{dashboard.summary.category_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-orange-500/30 bg-orange-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Recently Added</p>
                  <p className="text-2xl font-bold">{dashboard.summary.recently_added_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(val) => {
        setActiveTab(val);
        if (val === 'usage' && !usageHistory) {
          fetchUsageHistory();
        }
      }}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="categories">By Category</TabsTrigger>
          <TabsTrigger value="expensive">Most Expensive</TabsTrigger>
          <TabsTrigger value="recent">Recently Added</TabsTrigger>
          <TabsTrigger value="usage">Usage History</TabsTrigger>
          {searchResults && <TabsTrigger value="search">Search Results</TabsTrigger>}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Spending by Category Chart */}
          {spending && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                  Spending by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {spending.by_category.slice(0, 10).map((cat, idx) => (
                    <div key={cat.category} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{cat.category}</span>
                        <span className="text-muted-foreground">
                          {formatCurrency(cat.value)} ({cat.percentage}%)
                        </span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all"
                          style={{ width: `${Math.min(cat.percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Price Distribution */}
          {spending && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Box className="h-5 w-5 text-blue-400" />
                  Parts by Price Range
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-4">
                  {spending.by_price_range.map((range) => (
                    <div key={range.range} className="text-center p-4 rounded-lg bg-secondary/30">
                      <p className="text-xs text-muted-foreground mb-1">{range.range}</p>
                      <p className="text-lg font-bold">{range.count.toLocaleString()}</p>
                      <p className="text-xs text-green-400">{formatCurrency(range.value)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          {dashboard?.by_category?.map((cat) => (
            <Card key={cat.name} className="border-border/50">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{cat.name}</CardTitle>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">{cat.count} parts</span>
                    <span className="text-green-400 font-bold">{formatCurrency(cat.total_value)}</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {cat.parts.length > 0 ? (
                  <div className="space-y-2">
                    {cat.parts.map((part) => (
                      <div key={part.id} className="flex justify-between items-center p-2 rounded bg-secondary/30">
                        <div>
                          <p className="font-medium text-sm">{part.description}</p>
                          <p className="text-xs text-muted-foreground">{part.number}</p>
                        </div>
                        <span className="text-green-400 font-bold">{formatCurrency(part.cost)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No parts with cost data in this category</p>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Most Expensive Tab */}
        <TabsContent value="expensive">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-400" />
                Top 25 Most Expensive Parts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {dashboard?.top_expensive?.map((part, idx) => (
                  <div 
                    key={part.id} 
                    className={`flex justify-between items-center p-3 rounded-lg ${
                      idx < 3 ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-secondary/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx < 3 ? 'bg-yellow-500 text-black' : 'bg-secondary text-muted-foreground'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium">{part.description}</p>
                        <p className="text-xs text-muted-foreground">{part.number} • {part.category}</p>
                      </div>
                    </div>
                    <span className={`font-bold ${idx < 3 ? 'text-yellow-400 text-lg' : 'text-green-400'}`}>
                      {formatCurrency(part.cost)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recently Added Tab */}
        <TabsContent value="recent">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-400" />
                Recently Added Parts (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.recently_added?.length > 0 ? (
                <div className="space-y-2">
                  {dashboard.recently_added.map((part) => (
                    <div key={part.id} className="flex justify-between items-center p-3 rounded-lg bg-secondary/30">
                      <div className="flex-1">
                        <p className="font-medium">{part.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {part.number} • Added {new Date(part.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={`text-sm font-bold ${
                            part.quantity_on_hand > 0 ? 'text-blue-400' : 'text-muted-foreground'
                          }`}>
                            {part.quantity_on_hand !== null ? (
                              <>
                                {part.quantity_on_hand} <span className="text-xs font-normal">on hand</span>
                              </>
                            ) : (
                              <span className="text-xs">-</span>
                            )}
                          </p>
                          {part.locations && part.locations.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {part.locations.map(l => `${l.name}: ${l.quantity}`).join(', ')}
                            </p>
                          )}
                        </div>
                        <span className="text-green-400 font-bold min-w-[80px] text-right">{formatCurrency(part.cost)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No parts added in the last 30 days</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Usage History Tab */}
        <TabsContent value="usage" className="space-y-6">
          {loadingUsage ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : usageHistory ? (
            <>
              {/* Monthly Spending Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-400" />
                    Monthly Parts Spending
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-6 gap-2">
                    {usageHistory.monthly_parts_spending.slice(0, 6).map((month) => (
                      <div key={month.month} className="text-center p-3 rounded-lg bg-secondary/30">
                        <p className="text-xs text-muted-foreground">{month.month}</p>
                        <p className="text-lg font-bold text-green-400">{formatCurrency(month.total)}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top Used Parts */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5 text-purple-400" />
                    Most Frequently Used Parts ({usageHistory.unique_parts_used} unique parts from {usageHistory.total_service_entries} service entries)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {usageHistory.top_used_parts.slice(0, 20).map((part, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 rounded-lg bg-secondary/30">
                        <div className="flex items-center gap-3 flex-1">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            idx < 3 ? 'bg-purple-500 text-white' : 'bg-secondary text-muted-foreground'
                          }`}>
                            {idx + 1}
                          </span>
                          <div>
                            <p className="font-medium">{part.description}</p>
                            <p className="text-xs text-muted-foreground">
                              Used {part.usage_count}x across {part.vehicle_count} vehicle{part.vehicle_count !== 1 ? 's' : ''} • 
                              Last: {part.last_used}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-bold">{formatCurrency(part.total_cost)}</p>
                          <p className="text-xs text-muted-foreground">avg {formatCurrency(part.avg_cost)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Vehicle Parts Spending */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-orange-400" />
                    Parts Spending by Vehicle
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {usageHistory.vehicle_parts_spending.slice(0, 12).map((vehicle) => (
                      <div key={vehicle.vehicle} className="p-3 rounded-lg bg-secondary/30 text-center">
                        <p className="font-bold text-lg">{vehicle.vehicle}</p>
                        <p className="text-green-400 font-bold">{formatCurrency(vehicle.parts_cost)}</p>
                        <p className="text-xs text-muted-foreground">{vehicle.parts_count} parts</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <p className="text-muted-foreground text-center py-8">Click to load usage history</p>
          )}
        </TabsContent>

        {/* Search Results Tab */}
        {searchResults && (
          <TabsContent value="search">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-blue-400" />
                  Search Results for "{searchResults.query}" ({searchResults.count} found)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {searchResults.results.length > 0 ? (
                  <div className="space-y-2">
                    {searchResults.results.map((part) => (
                      <div key={part.id} className="flex justify-between items-center p-3 rounded-lg bg-secondary/30">
                        <div>
                          <p className="font-medium">{part.description}</p>
                          <p className="text-xs text-muted-foreground">{part.number} • {part.category}</p>
                        </div>
                        <span className="text-green-400 font-bold">{formatCurrency(part.cost)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No parts found matching your search</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
